import { groupBy, throttle } from "lodash";
import axios from "axios";
import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
} from "react";
import { AppContext } from "../AppContext";
import { isDraftTableOrder } from "./draftOrderUtils";
import { isTakeawayRouteTableName } from "../utils/floorUtils";
import { useCustomers } from "./hooks/useCustomer";
import {
    useGetDocTypeListShort,
    useGetDocTypeListWithChildren,
} from "./hooks/useDocTypesData";
import { useAddons, useItemGroups, useItems } from "./hooks/useItemsData";
import { useGetTableOrders } from "./hooks/useOrdersData";
import { useSearchPOSOpening } from "./hooks/usePOSData";
import { useGetUserDefaults } from "./hooks/useUser";

export const PosContext = createContext();

const TABLE_ORDER_FALLBACK_POLL_INTERVAL_MS = 10000;
const TABLE_ORDER_SAFETY_POLL_INTERVAL_MS = 30000;

const getTimestampValue = (value) => {
    if (!value) return null;
    if (typeof value === "string") return value;
    if (value instanceof Date) return value.toISOString();
    return String(value);
};

export const PosProvider = ({ children }) => {
    const {
        isKitchenUser,
        company,
        isTabsPos,
        multipleCompanies,
        socket,
        menuAllowedGroups,
        userInfo,
        posProfile,
        posProfiles,
        setPosProfile,
    } = useContext(AppContext);

    // Always filter by company when we have one, regardless of multi-company mode
    const tableOrdersQueryArgs = {
        company: company?.name,
        posProfile: posProfile?.name,
        disabled: !company?.name,
    };

    // NOTE: keep the call order stable, only the params change.
    const {
        data: tableOrders,
        isFetched,
        refetch: rawRefetchTablesData,
    } = useGetTableOrders(tableOrdersQueryArgs);

    // Frontend-only safety net for the takeaway list. The backend's
    // `get_floor_data` filters rooms by the POS Profile's allowed rooms +
    // company; if the takeaway table happens to live in a room that does not
    // satisfy that filter (e.g. its room has no company set, or the room is
    // not listed in `rm_pos_profile_rooms`), the takeaway bucket comes back
    // empty and `TakeawayOrdersList` shows nothing. To stay resilient against
    // those data quirks, fetch attending Table Orders for the linked
    // `posProfile.takeaway_table` directly and merge them into the resolved
    // takeaway table below.
    const {
        data: takeawayOrdersData,
        refetch: rawRefetchTakeawayOrders,
    } = useGetDocTypeListWithChildren({
        doctype: posProfile?.takeaway_table ? "Table Order" : null,
        filters: posProfile?.takeaway_table
            ? [
                  ["table", "=", posProfile.takeaway_table],
                  ["status", "=", "Attending"],
                  ["docstatus", "!=", 2],
              ]
            : null,
        order_by: "creation desc",
    });
    const rawRefetchTakeawayOrdersRef = useRef(rawRefetchTakeawayOrders);
    rawRefetchTakeawayOrdersRef.current = rawRefetchTakeawayOrders;

    // Throttled refetch with promise fan-out to preserve callers using .then()
    const throttledRefetchRef = useRef(null);
    const pendingResolversRef = useRef([]);
    // react-query's refetch identity can change after invalidations; if we put
    // rawRefetchTablesData in a useEffect dependency, that effect's cleanup runs
    // on every identity change and would reject pending refetch promises with
    // "PosContext unmounted" even though PosProvider is still mounted.
    const rawRefetchTablesDataRef = useRef(rawRefetchTablesData);
    rawRefetchTablesDataRef.current = rawRefetchTablesData;

    const refetchTablesData = useCallback(() => {
        return new Promise((resolve, reject) => {
            pendingResolversRef.current.push({ resolve, reject });
            if (throttledRefetchRef.current) {
                throttledRefetchRef.current();
            }
        });
    }, []);

    useEffect(() => {
        throttledRefetchRef.current = throttle(
            () => {
                // Fire the takeaway-orders refetch alongside the floor-data
                // refetch so the merged takeaway list stays in sync after any
                // order mutation. We do not block on its result.
                try {
                    rawRefetchTakeawayOrdersRef.current?.();
                } catch (_) {
                    /* no-op */
                }
                rawRefetchTablesDataRef
                    .current()
                    .then((result) => {
                        const pending = pendingResolversRef.current.splice(0);
                        pending.forEach(({ resolve }) => resolve(result));
                    })
                    .catch((error) => {
                        const pending = pendingResolversRef.current.splice(0);
                        pending.forEach(({ reject }) => reject(error));
                    });
            },
            1000,
            { leading: true, trailing: true }
        );

        return () => {
            if (
                throttledRefetchRef.current &&
                throttledRefetchRef.current.cancel
            ) {
                throttledRefetchRef.current.cancel();
            }
            // Reject any pending promises only when PosProvider actually unmounts
            const pendingOnUnmount = pendingResolversRef.current.splice(0);
            pendingOnUnmount.forEach(({ reject }) =>
                reject(new Error("PosContext unmounted"))
            );
        };
        // Intentionally empty: throttle is stable; latest refetch via ref above.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const { data: getDefaultRoom, refetch: refetchDefaultRoom } =
        useGetUserDefaults("Room");

    const {
        data: menuItems,
        isFetched: isMenuItemsFetched,
        refetch: refetchMenuItems,
    } = useItems({ allowedGroups: menuAllowedGroups });

    useEffect(() => {
        refetchMenuItems();
    }, []);

    const { data: addonsItems } = useAddons();
    const { data: groupedItems } = useItemGroups({
        allowedGroups: menuAllowedGroups,
    });

    const { data: customers, refetch: refetchCustomers } = useCustomers();

    const { data: todayOpen } = useSearchPOSOpening({ user: userInfo.name });
    const [todayPosName, setTodayPosName] = useState(null);
    const { data: posEntryDetails } = useSearchPOSOpening({
        user: userInfo.name,
    });

    const [addresses, setAddresses] = useState(null);
    const [territoryOptions, setTerritoryOptions] = useState([""]);
    const [selectedAddress, setSelectedAddress] = useState(null);

    const { data: getAddresses, refetch: refetchAddresses } =
        useGetDocTypeListShort("Address", ["*"]);
    const { data: getTerritoryOptions } = useGetDocTypeListShort("Territory");

    const [rooms, setRooms] = useState({});
    const [selectedRoom, setSelectedRoom] = useState(null);
    const [posProfileCompanyMismatch, setPosProfileCompanyMismatch] =
        useState(null);

    const [tables, setTables] = useState([]);
    const [selectedTable, setSelectedTable] = useState(null);

    const [takeawayTable, setTakeawayTable] = useState(null);

    const [orders, setOrders] = useState([]);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [orderItems, setOrderItems] = useState([]);

    // Latest selectedOrder accessible inside memoized callbacks without
    // bloating their dependency list (avoids identity churn → effect spam).
    const selectedOrderRef = useRef(selectedOrder);
    selectedOrderRef.current = selectedOrder;
    // Guard against re-issuing the same direct-fetch while one is in-flight.
    const inflightOrderFetchRef = useRef(null);

    const [selectedCustomer, setSelectedCustomer] = useState(null);

    const discountEnabled = true;

    const [isAppLoading, setAppLoading] = useState(true);
    const [isTableOrderSocketConnected, setIsTableOrderSocketConnected] =
        useState(false);
    const lastOrderEditTimestampRef = useRef(null);

    useEffect(() => {
        if (getAddresses) {
            setAddresses(
                groupBy(getAddresses, ({ address_title }) => address_title)
            );
        }
    }, [getAddresses]);

    useEffect(() => {
        if (getTerritoryOptions) {
            const tmpTerritory = [...getTerritoryOptions];
            setTerritoryOptions(tmpTerritory.map((i) => i.name));
        }
    }, [getTerritoryOptions]);

    useEffect(() => {
        if (todayOpen) {
            if (todayOpen.length > 0) {
                setTodayPosName(todayOpen[0].name);
            } else {
                setTodayPosName(null);
            }
        }
    }, [todayOpen]);

    useEffect(() => {
        if (selectedOrder) {
            setOrderItems(selectedOrder.entry_items || []);
        } else {
            setOrderItems([]);
        }
    }, [selectedOrder]);

    useEffect(() => {
        if (tableOrders) {
            const roomsData = tableOrders.message?.rooms || {};
            setRooms(roomsData);

            // The POS Profile may pin a specific Restaurant Object as the takeaway table
            // (`takeaway_table` link). The backend `get_floor_data` may still emit its
            // legacy `message.takeaway` bucket (or none at all), so resolve in this order:
            //   1. The room table whose `name` matches `posProfile.takeaway_table`.
            //   2. The legacy backend bucket `message.takeaway`.
            //   3. A minimal stub built from the POS Profile link so the POS cart can
            //      mount for a brand-new takeaway order even before the backend
            //      surfaces it in floor data.
            const linkedName = posProfile?.takeaway_table;
            let resolvedTakeaway = tableOrders.message?.takeaway || null;
            if (linkedName) {
                const roomTables = Object.values(roomsData).flatMap((room) =>
                    Object.values(room?.tables || {})
                );
                const matched = roomTables.find(
                    (tbl) => tbl?.name === linkedName
                );
                if (matched) {
                    resolvedTakeaway = matched;
                } else if (
                    !resolvedTakeaway ||
                    resolvedTakeaway.name !== linkedName
                ) {
                    resolvedTakeaway = {
                        name: linkedName,
                        tableDescription: linkedName,
                        reserved: 0,
                        orders: {},
                    };
                }
            }

            // Merge the directly-fetched attending takeaway orders so the
            // takeaway list stays correct even when the backend's room-scoped
            // floor data does not surface them. Fetched orders win on key
            // collision because they are more authoritative for this table.
            if (resolvedTakeaway && Array.isArray(takeawayOrdersData)) {
                const onlyForLinked = linkedName
                    ? takeawayOrdersData.filter(
                          (o) => o?.table === linkedName
                      )
                    : takeawayOrdersData;
                if (onlyForLinked.length > 0) {
                    const mergedOrders = { ...(resolvedTakeaway.orders || {}) };
                    onlyForLinked.forEach((order) => {
                        if (order?.name) {
                            mergedOrders[order.name] = order;
                        }
                    });
                    resolvedTakeaway = {
                        ...resolvedTakeaway,
                        orders: mergedOrders,
                        numberOfOrders: Object.keys(mergedOrders).length,
                    };
                }
            }

            setTakeawayTable(resolvedTakeaway);

            const prevRoomName = selectedRoom?.name;

            // Room preference order:
            // 1) keep current selected room (if still exists)
            // 2) user's default room (if configured and exists)
            // 3) first room in available rooms list
            const keepRoom =
                prevRoomName &&
                Object.values(roomsData).find((r) => r?.name === prevRoomName);

            const defaultRoomName =
                getDefaultRoom?.message && getDefaultRoom.message.length > 0
                    ? getDefaultRoom.message[0]
                    : null;
            const defaultRoom =
                defaultRoomName &&
                Object.values(roomsData).find((r) => r?.name === defaultRoomName);

            const firstRoom = Object.values(roomsData)[0];

            const nextRoom = keepRoom || defaultRoom || firstRoom || null;
            // Always refresh selected room object reference so tables/orders update without changing the selected room.
            setSelectedRoom(nextRoom);
        }
    }, [
        tableOrders,
        getDefaultRoom,
        selectedRoom,
        posProfile?.takeaway_table,
        takeawayOrdersData,
    ]);

    useEffect(() => {
        const roomCompany = selectedRoom?.company || null;
        if (!roomCompany) {
            setPosProfileCompanyMismatch(null);
            return;
        }

        if (posProfile?.company === roomCompany) {
            setPosProfileCompanyMismatch(null);
            return;
        }

        const sameCompanyProfile = (posProfiles || []).find(
            (profile) => profile.company === roomCompany
        );

        if (sameCompanyProfile) {
            if (sameCompanyProfile.name !== posProfile?.name) {
                setPosProfile(sameCompanyProfile.name, true);
            }
            setPosProfileCompanyMismatch(null);
            return;
        }

        setPosProfileCompanyMismatch({
            roomCompany,
            message: `No POS Profile found for company "${roomCompany}". Please create/select a matching POS Profile.`,
        });
    }, [
        selectedRoom?.name,
        selectedRoom?.company,
        posProfile?.name,
        posProfile?.company,
        posProfiles,
        setPosProfile,
    ]);

    useEffect(() => {
        if (selectedRoom && selectedRoom.tables) {
            setTables(Object.values(selectedRoom.tables));
        } else {
            setTables([]);
        }
    }, [selectedRoom]);

    // POS must work for takeaway-only flows and direct `/pos/...` links even when no
    // dining room exists in floor data (`selectedRoom` stays null). Previously we gated
    // on `selectedRoom`, which left `isAppLoading` true forever → PosPage never ran
    // table selection and `OrderCart` never mounted.
    useEffect(() => {
        if (isMenuItemsFetched && isFetched) {
            setAppLoading(false);
        }
    }, [isMenuItemsFetched, isFetched]);

    useEffect(() => {
        if (orders && orders.length > 0) {
        } else {
            setSelectedOrder(null);
        }
    }, [orders]);

    useEffect(() => {
        lastOrderEditTimestampRef.current = null;
    }, [tableOrdersQueryArgs.company, tableOrdersQueryArgs.posProfile]);

    const getMenuItem = (item) => {
        if (menuItems) {
            return menuItems.find((i) => i.item_code === item);
        } else {
            return null;
        }
    };

    const changeSelectedTable = useCallback((tableName, orderName) => {
        if (isTabsPos) {
            setSelectedTable(tableOrders?.message.takeaway);
            setOrders(
                Object.values(tableOrders?.message?.takeaway?.orders || {})
            );
            return;
        }

        if (!tableName) {
            setOrders(null);
            return;
        }

        // Takeaway lives under floor `message.takeaway`, not under `selectedRoom.tables`.
        // Must not require `tables.length > 0` or the cart never mounts on direct POS links.
        if (isTakeawayRouteTableName(tableName, posProfile, takeawayTable)) {
            if (!takeawayTable) {
                return;
            }
            const tw = takeawayTable;
            setSelectedTable(tw);
            if (orderName && tw.orders?.[orderName]) {
                setSelectedOrder(tw.orders[orderName]);
            } else if (!orderName && tw.orders) {
                const list = Object.values(tw.orders);
                setSelectedOrder(list.length ? list[0] : null);
            } else {
                setSelectedOrder(null);
            }
            if (tw.orders) {
                setOrders(Object.values(tw.orders));
            } else {
                setOrders(null);
            }
            return;
        }

        if (!tables.length) {
            setOrders(null);
            return;
        }

        const newTable = tables.find(
            (i) =>
                i.tableDescription === tableName || i.name === tableName
        );

        if (!newTable) {
            return;
        }

        setSelectedTable(newTable);
        if (orderName && newTable.orders?.[orderName]) {
            setSelectedOrder(newTable.orders[orderName]);
        } else if (!orderName && newTable.orders) {
            const list = Object.values(newTable.orders);
            setSelectedOrder(list.length ? list[0] : null);
        }

        if (newTable.orders) {
            setOrders(Object.values(newTable.orders));
        }
    }, [isTabsPos, tableOrders, posProfile, takeawayTable, tables]);

    const setTakeawayOrder = useCallback((orderName) => {
        if (orderName) {
            if (takeawayTable) {
                const takeawayOrder = takeawayTable.orders[orderName];
                setSelectedTable(takeawayTable);
                if (takeawayOrder) {
                    setSelectedOrder(takeawayOrder);
                    return;
                }

                // Already loaded this order via a previous direct fetch — no need to refetch.
                if (selectedOrderRef.current?.name === orderName) {
                    return;
                }

                // A request for the same order is already in flight — bail to avoid spam.
                if (inflightOrderFetchRef.current === orderName) {
                    return;
                }

                // Draft orders are not included in floor data (filtered).
                // Detect which doctype to fetch from based on the name prefix.
                const doctype = isDraftTableOrder(orderName)
                    ? "Draft Table Order"
                    : "Table Order";
                inflightOrderFetchRef.current = orderName;
                axios
                    .get("method/frappe.client.get", {
                        params: { doctype, name: orderName },
                    })
                    .then(({ data }) => {
                        if (data?.message) {
                            setSelectedOrder(data.message);
                            setOrders((prev) => {
                                const list = Array.isArray(prev) ? prev : [];
                                const exists = list.some(
                                    (o) => o?.name === data.message.name
                                );
                                return exists ? list : [data.message, ...list];
                            });
                        } else {
                            setSelectedOrder(null);
                        }
                    })
                    .catch(() => {
                        setSelectedOrder(null);
                    })
                    .finally(() => {
                        if (inflightOrderFetchRef.current === orderName) {
                            inflightOrderFetchRef.current = null;
                        }
                    });
            }
        } else {
            // New takeaway order (no order id in URL) — wait for floor payload;
            // avoid clearing selection while takeawayTable is still null.
            if (takeawayTable) {
                setSelectedTable(takeawayTable);
                setSelectedOrder(null);
            }
        }
    }, [takeawayTable]);

    useEffect(() => {
        if (!socket) {
            setIsTableOrderSocketConnected(false);
            return;
        }

        setIsTableOrderSocketConnected(!!socket.connected);

        socket.emit("doctype_subscribe", "Table Order");

        const onConnect = () => {
            setIsTableOrderSocketConnected(true);
            socket.emit("doctype_subscribe", "Table Order");
        };

        const onDisconnect = () => {
            setIsTableOrderSocketConnected(false);
        };

        const onListUpdate = (data) => {
            if (data?.doctype === "Table Order") {
                // Throttled in refetchTablesData to prevent floods.
                refetchTablesData();
            }
        };

        socket.on("connect", onConnect);
        socket.on("disconnect", onDisconnect);
        socket.on("connect_error", onDisconnect);
        socket.on("list_update", onListUpdate);

        return () => {
            // Prevent accumulating listeners which can amplify refetching and cause visible "hard refresh" flicker.
            if (socket.off) {
                socket.off("connect", onConnect);
                socket.off("disconnect", onDisconnect);
                socket.off("connect_error", onDisconnect);
                socket.off("list_update", onListUpdate);
            } else if (socket.removeListener) {
                socket.removeListener("connect", onConnect);
                socket.removeListener("disconnect", onDisconnect);
                socket.removeListener("connect_error", onDisconnect);
                socket.removeListener("list_update", onListUpdate);
            }
        };
    }, [socket, refetchTablesData]);

    useEffect(() => {
        if (tableOrdersQueryArgs.disabled) return;

        let cancelled = false;
        let intervalId = null;
        const pollInterval = isTableOrderSocketConnected
            ? TABLE_ORDER_SAFETY_POLL_INTERVAL_MS
            : TABLE_ORDER_FALLBACK_POLL_INTERVAL_MS;

        const pollLastOrderEditTimestamp = async () => {
            try {
                const params = {};
                if (tableOrdersQueryArgs.company) {
                    params.company = tableOrdersQueryArgs.company;
                }
                const { data } = await axios.get(
                    "method/restaurant_management.api.get_last_order_edit_timestamp",
                    { params }
                );
                if (cancelled) return;

                const nextTimestamp = getTimestampValue(data?.message);
                if (!nextTimestamp) return;

                const previousTimestamp = lastOrderEditTimestampRef.current;
                lastOrderEditTimestampRef.current = nextTimestamp;

                if (
                    previousTimestamp &&
                    previousTimestamp !== nextTimestamp
                ) {
                    refetchTablesData();
                }
            } catch (error) {
                // Keep polling; transient failures should not disable table-order sync.
                console.warn(
                    "Failed to poll latest table order timestamp",
                    error
                );
            }
        };

        pollLastOrderEditTimestamp();
        intervalId = window.setInterval(
            pollLastOrderEditTimestamp,
            pollInterval
        );

        return () => {
            cancelled = true;
            if (intervalId) {
                window.clearInterval(intervalId);
            }
        };
    }, [
        tableOrdersQueryArgs.company,
        tableOrdersQueryArgs.disabled,
        socket,
        isTableOrderSocketConnected,
        refetchTablesData,
    ]);

    return (
        <PosContext.Provider
            value={{
                tables,
                selectedTable,
                setSelectedTable,
                changeSelectedTable,
                orders,
                setOrders,
                tableOrders,
                isAppLoading,
                selectedOrder,
                setSelectedOrder,
                orderItems,
                menuItems,
                getMenuItem,
                customerContext: [selectedCustomer, setSelectedCustomer],
                setSelectedCustomer,
                selectedRoom,
                takeawayTable,
                customers,
                refetchCustomers,
                setTakeawayOrder,
                addonsItems,
                groupedItems,
                roomContext: [selectedRoom, setSelectedRoom],
                roomOptions: rooms,
                todayOpen,
                posEntryDetails,
                refetchTablesData,
                addressContext: [selectedAddress, setSelectedAddress],
                addresses,
                refetchAddresses,
                territoryOptions,
                refetchDefaultRoom,
                discountEnabled,
                socket,
                posProfileCompanyMismatch,
            }}
        >
            {isKitchenUser ? (
                children
            ) : isFetched ? (
                children
            ) : (
                <div>Loading</div>
            )}
            {/* {children} */}
        </PosContext.Provider>
    );
};
